import AlertCircleOutline from '@mdi/svg/svg/alert-circle-outline.svg?react';
import UnarchiveIcon from '@mdi/svg/svg/archive-arrow-up.svg?react';
import ArchiveIcon from '@mdi/svg/svg/archive.svg?react';
import CancelScheduleIcon from '@mdi/svg/svg/clock-remove-outline.svg?react';
import CloudUploadIcon from '@mdi/svg/svg/cloud-upload.svg?react';
import CopyIcon from '@mdi/svg/svg/content-copy.svg?react';
import SidekickIcon from '@mdi/svg/svg/creation.svg?react';
import DownloadIcon from '@mdi/svg/svg/download.svg?react';
import ReopenScheduleIcon from '@mdi/svg/svg/history.svg?react';
import NoteEditIcon from '@mdi/svg/svg/text-box-edit-outline.svg?react';
import { type EventFlow } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { Button, Flex, MDIIcon } from '@solinkcorp/solink-components';
import Print from '@solinkcorp/solink-components/svg/print.svg?react';
import { type IEvent } from 'clients/PlatformApi';
import CloudStatus from 'components/CloudStatus/CloudStatus';
import ExternalVmsMenu from 'components/ExternalVmsMenu/ExternalVmsMenu';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React from 'react';
import { isKiosk } from 'src/core/utils/kioskUtils';
import useLocale from 'src/hooks/useLocale';
import * as EventUtil from 'utils/EventUtil';
import { parseExternalVms } from 'utils/externalVms';
import FeaturesUtil from 'utils/FeaturesUtil';
import { EVENT_STATUS_CLOSED } from 'vd-constants';
import EventMenuActions from './EventMenuActions';
import EventVerifyButton from './EventVerifyButton/EventVerifyButton';
import useCanEventBeVerified from './EventVerifyButton/useCanEventBeVerified';
import useEventMenuActions from './useEventMenuActions';

interface EventActionsRowProps {
  allowEdit: boolean;
  closeScheduledEvent: () => void;
  colorMode: 'light' | 'dark' | undefined;
  enterEditMode: () => void;
  event: IEvent;
  eventColor: string;
  eventDescription: string;
  favourite: boolean;
  hideActionMenu?: boolean;
  isAlarms: boolean | undefined;
  isClips: boolean | undefined;
  removeEvent: ((targetEvent: IEvent) => void) | undefined;
  reopenScheduledEvent: () => void;
  tenantId: string;
  toggleEventFavouriteMenu: () => void;
  toggleVerifySection: () => void;
  updateEvent: ((newEvent: IEvent) => void) | undefined;
  verificationWorkflow: EventFlow | undefined;
  isEventSpotCheck: boolean;
  toggleEventSpotCheckSection: () => void;
  hasAgentFindings: boolean;
  toggleAgentFindingsSection: () => void;
}

const EventActionsRow: React.FC<EventActionsRowProps> = (props) => {
  const {
    allowEdit,
    closeScheduledEvent,
    colorMode = 'light',
    enterEditMode,
    event,
    eventColor,
    eventDescription,
    favourite,
    hideActionMenu,
    isAlarms,
    isClips,
    removeEvent,
    reopenScheduledEvent,
    tenantId,
    toggleEventFavouriteMenu,
    updateEvent,
    toggleVerifySection,
    verificationWorkflow,
    isEventSpotCheck,
    toggleEventSpotCheckSection,
    hasAgentFindings,
    toggleAgentFindingsSection,
  } = props;

  const locale = useLocale();
  const { accessToken } = useUserSession();

  const eventMenuActionsProps = useEventMenuActions({
    event,
    isAlarms,
    isClips,
    updateEvent,
    removeEvent,
  });
  const { isSaveClipMenuVisible, handleOpenPrintPanel, handleOpenSavePanel, handleCopyShareLink } =
    eventMenuActionsProps;

  const isScheduled = EventUtil.isScheduledActivity(event);
  const isInFuture = EventUtil.isInFuture(event);
  const isCopyShareLinkVisible =
    EventUtil.isShared(event) && EventUtil.isPushToCloudComplete(event) && !isKiosk();
  const isClosed = event.state === EVENT_STATUS_CLOSED;

  const { canBeVerified } = useCanEventBeVerified(event, verificationWorkflow, isAlarms);

  const openDownload = () => {
    // Dispatch quick viewer event as a fix to the issue where a user couldn't download if they haven't viewed video before (need the cookie from Connect for authorization)
    const mouseOverEvent = new MouseEvent('mouseover', {
      view: window,
      bubbles: true,
      cancelable: true,
    });

    // TODO: This is very fragile, as it relies on EventQuickViewer's ID never changing.
    const quickPlayer = document.getElementById(`qp-${event.id}`);
    if (quickPlayer) {
      quickPlayer.dispatchEvent(mouseOverEvent);

      setTimeout(() => {
        // Stop the video being played in the quick viewer
        const mouseOutEvent = new MouseEvent('mouseout', {
          view: window,
          bubbles: true,
          cancelable: true,
        });

        quickPlayer.dispatchEvent(mouseOutEvent);
      }, 500);
    }

    handleOpenSavePanel({ saveMode: false, saveExistingEvent: true });
    AnalyticsLogger.openDownload({ event });
  };

  let stateChangeButtonText = '';
  let stateChangeButtonIcon;
  if (isInFuture) {
    stateChangeButtonText = isClosed ? locale.event.reopenScheduled : locale.event.cancelScheduled;
    stateChangeButtonIcon = isClosed ? ReopenScheduleIcon : CancelScheduleIcon;
  } else {
    stateChangeButtonText = isClosed ? locale.general.unarchive : locale.notifications.archive;
    stateChangeButtonIcon = isClosed ? UnarchiveIcon : ArchiveIcon;
  }
  const changeState = isClosed ? reopenScheduledEvent : closeScheduledEvent;

  const shouldRenderCloudStatus =
    EventUtil.isSharedMotion(event) ||
    EventUtil.isSharedTransaction(event) ||
    EventUtil.isSharedBookmark(event);
  const shouldRenderDownload = EventUtil.isDownloadBookmark(event) && FeaturesUtil.canDownload();

  const { hasExpired } = EventUtil.checkDownloadEventExpiration(event, locale);

  let clipTitleClickHandler: (() => void) | undefined;
  if (shouldRenderCloudStatus) {
    clipTitleClickHandler = () =>
      handleOpenSavePanel({
        saveMode: false,
        saveExistingEvent: true,
      });
  } else if (shouldRenderDownload) {
    clipTitleClickHandler = openDownload;
  } else {
    clipTitleClickHandler = undefined;
  }

  const externalVms = parseExternalVms(event.details?.externalVms);

  return (
    <Flex className="actions-wrapper" alignItems="center">
      {externalVms && (
        <ExternalVmsMenu vms={externalVms} accessToken={accessToken} colorMode={colorMode} />
      )}
      {isEventSpotCheck && (
        <Button
          dataTestId="clip-sidekick-response"
          onClick={toggleEventSpotCheckSection}
          icon={<MDIIcon icon={SidekickIcon} size={18} />}
          iconOnly
          tooltip={locale.scheduledAIChat.viewSidekickAnswers}
          type="clear"
          colorMode={colorMode}
        />
      )}
      {hasAgentFindings && (
        <Button
          dataTestId="clip-agent-findings"
          onClick={toggleAgentFindingsSection}
          icon={<MDIIcon icon={SidekickIcon} size={18} />}
          iconOnly
          tooltip={locale.event.viewAgentFindings}
          type="clear"
          colorMode={colorMode}
        />
      )}
      {!allowEdit && (
        <Button
          className="id-invalid-warning"
          dataTestId="events-id-invalid-warning"
          icon={<MDIIcon icon={AlertCircleOutline} size={19} fill="var(--color-red)" />}
          iconOnly
          tooltip={locale.event.invalidIdWarning}
          type="clear"
          colorMode={colorMode}
        />
      )}
      {canBeVerified && (
        <EventVerifyButton
          verifiedIconFill={eventColor}
          onClick={toggleVerifySection}
          colorMode={colorMode}
          event={event}
          size={18}
        />
      )}
      {allowEdit && (
        <Button
          dataTestId="clip-edit"
          onClick={enterEditMode}
          icon={<MDIIcon icon={NoteEditIcon} size={18} />}
          iconOnly
          tooltip={
            eventDescription.length > 0 ? locale.event.actionEditNotes : locale.event.actionAddNotes
          }
          type="clear"
          colorMode={colorMode}
        />
      )}
      {isSaveClipMenuVisible && !isScheduled && (
        <Button
          dataTestId="clip-save"
          onClick={() => handleOpenSavePanel({ saveMode: true, saveExistingEvent: true })}
          icon={<MDIIcon icon={CloudUploadIcon} size={18} />}
          iconOnly
          tooltip={locale.videoPlayerOptions.saveClip}
          type="clear"
          colorMode={colorMode}
        />
      )}
      {isScheduled && (
        <Button
          dataTestId="clip_schedule-activty"
          onClick={changeState}
          tooltip={stateChangeButtonText}
          icon={<MDIIcon icon={stateChangeButtonIcon} size={18} />}
          iconOnly
          type="clear"
          colorMode={colorMode}
        />
      )}
      {EventUtil.isDataOnly(event) && !isScheduled && (
        <Button
          className="view-event-button"
          dataTestId="clip-view-event"
          onClick={handleOpenPrintPanel}
          icon={<MDIIcon icon={Print} size={16} />}
          iconOnly={true}
          type="clear"
          tooltip={locale.print.printAction}
          colorMode={colorMode}
        />
      )}
      {isCopyShareLinkVisible && (
        <Button
          className="copy-share-link-button"
          dataTestId="events-copy-share-link"
          icon={<MDIIcon icon={CopyIcon} size={16} />}
          iconOnly
          tooltip={locale.event.copyShareLink}
          type="clear"
          onClick={handleCopyShareLink}
          colorMode={colorMode}
        />
      )}
      {(shouldRenderCloudStatus || (shouldRenderDownload && !hasExpired)) && (
        <div className="clip-status-wrapper" onClick={clipTitleClickHandler}>
          {shouldRenderCloudStatus && (
            <CloudStatus
              eventStatus={event.pushToCloudStatus}
              isInProgress={false}
              event={event}
              tenantId={tenantId}
              locationId={event.locationId}
              colorMode={colorMode}
            />
          )}
          {shouldRenderDownload && !hasExpired && (
            <Button
              dataTestId="events-download-button"
              icon={<MDIIcon icon={DownloadIcon} size={18} />}
              iconOnly={true}
              tooltip={locale.download.download}
              type="clear"
              onClick={clipTitleClickHandler}
              colorMode={colorMode}
            />
          )}
        </div>
      )}
      {!hideActionMenu && (
        <EventMenuActions
          {...eventMenuActionsProps}
          colorMode={colorMode}
          favourite={favourite}
          toggleEventFavouriteMenu={toggleEventFavouriteMenu}
          eventDescription={eventDescription}
          enterEditMode={enterEditMode}
          isAlarms={isAlarms}
          isClips={isClips}
        />
      )}
    </Flex>
  );
};

export default EventActionsRow;
